import { FeatureDefinition } from '../services/api';

/**
 * Does a feature match what someone typed into the features search box?
 *
 * Pure and separate from the component so the matching rules can be tested
 * without standing up MUI accordions — the component test then only has to
 * prove the wiring.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE ID IS SEARCHABLE, AND THAT IS THE WHOLE POINT.
 * ─────────────────────────────────────────────────────────────────────────
 * The list renders `name` and `description`; it has never rendered `id`. So a
 * Ctrl+F for `REPORT_TRANSFORM` — the name the flag has in the backend, in the
 * DynamoDB row, and in every conversation about it — matches nothing at all,
 * and the browser reports "0/0" on an org where the feature is sitting right
 * there under its display name, "Report Actions".
 *
 * That is not a hypothetical: it is how this box came to exist. Someone was
 * asked to enable REPORT_TRANSFORM for an org, opened the dialog, searched for
 * exactly that string, and could not find it among 71 checkboxes.
 *
 * SEPARATORS ARE IGNORED on both sides, so all of these find the same row:
 *
 *   REPORT_TRANSFORM      (the id, as written in code and in the DB)
 *   report transform      (the id, as a human would type it)
 *   report-transform      (the id, kebab-cased)
 *   Report Actions        (the display name)
 *   re-presents           (a phrase from the description)
 *
 * The category label is searchable too, so "assessment" narrows to that group
 * without needing to know which features are in it.
 */

/** Lower-case and drop every separator, so `_`, `-` and spaces stop mattering. */
function normalise(value: string): string {
  return value.toLowerCase().replace(/[\s_-]+/g, '');
}

export function matchesFeatureQuery(
  feature: Pick<FeatureDefinition, 'id' | 'name' | 'description'>,
  query: string,
  categoryLabel?: string,
): boolean {
  const q = normalise(query);
  // An empty or whitespace-only query matches everything — the list is
  // unfiltered, not empty. Getting this backwards blanks the dialog the moment
  // someone clears the box.
  if (!q) return true;

  const haystacks = [
    String(feature.id ?? ''),
    feature.name ?? '',
    feature.description ?? '',
    categoryLabel ?? '',
  ];

  return haystacks.some((h) => normalise(h).includes(q));
}

/**
 * Whether a query is actually filtering, as opposed to being empty/whitespace.
 *
 * Exported because three separate behaviours key off it — force-expanding the
 * accordions, hiding empty categories, and showing the id under each row — and
 * they must agree. Re-deriving `query.trim().length > 0` at each site is how
 * they drift.
 */
export function isFiltering(query: string): boolean {
  return normalise(query).length > 0;
}
